/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import * as React from 'react';
import { useState } from 'react';
import { Box, Text } from 'grommet';
import { ActionButton, BrandColors, LinkButton, LoneIconButton, MaterialIcon } from '@blend/enterprise-components';
import * as Ron from 'blb-ui-types/ron';
import { CredentialAttributes } from 'blb-components/AuthenticatedApp/RON/NotaryPage/CredentialAttributes';
import { CredentialTypeNames, UserCredentialType } from 'blb-constants/ron';

export namespace CredentialInfoCard {
  export interface Props {
    credential: Ron.API.UserCredential | undefined;
    onEditCredential: () => void;
    type: UserCredentialType;
  }
}

/** This component renders meta data for one credential as a card */
export const CredentialInfoCard: React.FC<CredentialInfoCard.Props> = ({ onEditCredential, type, credential }) => {
  const [collapsed, setCollapsed] = useState(false);
  const renderMissingCredentialCard = (type: UserCredentialType) => {
    return (
      <>
        <Box align="start" gap="xsmall">
          <Text id="it-credentialInfoCard-type" size="medium" weight="bold">
            {CredentialTypeNames[type]}
          </Text>
          <Box
            align="center"
            background="accentYellow70"
            direction="row"
            gap="xxsmall"
            pad={{ horizontal: 'xsmall', vertical: 'xxsmall' }}
          >
            <MaterialIcon color={BrandColors.ACCENT_YELLOW_DARK} size={11} type="warning" />
            <Text color="accentYellowDark" size="11px" weight="bold">
              MISSING CREDENTIAL
            </Text>
          </Box>
        </Box>
        <Box justify="center">
          <ActionButton
            btnSize="sm"
            id="it-addCredential-btn"
            label="Add credential"
            onClick={onEditCredential}
            theme="tertiary"
          />
        </Box>
      </>
    );
  };

  const renderCredentialMetaData = (credential: Ron.API.UserCredential) => {
    return (
      <>
        <Box gap="xsmall">
          <Box>
            <Text id="it-credentialInfoCard-type" size="medium" weight="bold">
              {CredentialTypeNames[credential.type]}
            </Text>
          </Box>
          {!collapsed && <CredentialAttributes credential={credential} sameRowKeyValueLayout={true} />}
        </Box>
        <Box>
          <Box direction="row" gap="small" justify="start">
            <LinkButton icon="edit" id="it-addCredential-btn" label="Edit" onClick={onEditCredential} />
            <LoneIconButton
              btnSize="sm"
              icon={collapsed ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
              id="it-credentialInfoCard-collapse-btn"
              noBorder={true}
              onClick={() => setCollapsed(!collapsed)}
              theme="tertiary"
            />
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box
      align="stretch"
      background="white"
      border={{ color: 'cloud70', size: '1px' }}
      direction="row"
      flex={{ shrink: 0 }}
      id={`it-credentialCard-${type}`}
      justify="between"
      key={type}
      pad="small"
      round="3px"
      width={{ max: 'large' }}
    >
      {credential ? renderCredentialMetaData(credential) : renderMissingCredentialCard(type)}
    </Box>
  );
};
